import { React, useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import "../index.css";

export default function FormPremisesDetails({
  prevStep,
  handleChange,
  form,
  setForm,
  setPremisesErrors,
  premisesErrors,
  unexpectedError,
  handleProductSelection,
  addressSuggest,
  addressSuggestions,
  setAddressSuggestions,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (!debouncedSearchTerm) return false;
    addressSuggest(debouncedSearchTerm, true);
  }, [debouncedSearchTerm, addressSuggest]);

  const onSubmitHandler = async () => {
    setAddressSuggestions([]);
    const postcodeRegEx = new RegExp(/^[0-9]+$/);
    const errors = {};

    if (form.premises_address_line_1.trim() === "") {
      errors.premises_address_line_1 = "Address Line 1 cannot be empty";
    }

    if (form.premises_address_line_1.trim().length < 3) {
      errors.premises_address_line_1 =
        "Address Line 1 cannot be less than 3 characters";
    }

    if (form.premises_suburb.trim() === "") {
      errors.premises_suburb = "Suburb cannot be empty";
    }

    if (form.premises_suburb.trim().length < 3) {
      errors.premises_suburb = "Suburb cannot be less than 3 characters";
    }

    if (!postcodeRegEx.test(form.premises_postcode)) {
      errors.premises_postcode = "Postcode must be numeric";
    }
    if (form.premises_postcode.trim() === "") {
      errors.premises_postcode = "Postcode cannot be empty";
    }

    setPremisesErrors(errors);

    if (Object.keys(errors).length === 0) {
      handleProductSelection(form.type);
    }
  };

  const mapAddress = (e) => {
    var address = addressSuggestions[e.target.id].address;
    var obj = {
      premises_address_line_1:
        (address.unitNumber ? address.unitNumber + "/" : "") +
        (address.firstNumber ? address.firstNumber : address.lotNumber ? "LOT " + address.lotNumber : "") +
        (address.lastNumber ? "-" + address.lastNumber : "") +
        ` ${address.streetName} ${
          address.streetType ? address.streetType : ""
        }`,
      premises_address_line_2: "",
      premises_suburb: address.suburb,
      premises_postcode: address.postcode,
    };
    setForm((current) => {
      return { ...current, ...obj };
    });
    setAddressSuggestions([]);
  };

  return (
    <div>
      <h4 className="text-2xl font-semibold">Premises Details</h4>
      <p className="leading-relaxed mt-1 text-gray-600 mb-10">
        Enter the property address for which the form is needed.
      </p>

      <form className="space-y-4 xs:w-[100%]">
        <div className="flex flex-wrap">
          <div className="w-full">
            <label
              className="block uppercase text-gray-700 text-xs font-bold mb-2"
              htmlFor="premises_address_line_1"
            >
              Address Line 1 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={
                premisesErrors.premises_address_line_1
                  ? " border-red-500 border-x border-y px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
                  : "border-0 px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
              }
              required
              placeholder="Address Line 1"
              name="premises_address_line_1"
              onChange={(e) => {
                handleChange("premises_address_line_1")(e);
                setSearchTerm(e.target.value);
              }}
              value={form.premises_address_line_1 || ""}
            />

            {addressSuggestions && addressSuggestions.length > 1 && (
              <div className="w-full border-gray-300 rounded">
                <div>
                  {addressSuggestions.map((address, i) => {
                    return (
                      <div
                        onClick={mapAddress}
                        key={i}
                        id={i}
                        className="px-2 py-1 bg-gray-100 hover:bg-sky-500 hover:text-white cursor-pointer"
                      >
                        {address.display}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className=" text-red-600 text-xs mt-2">
              {premisesErrors.premises_address_line_1}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div className="w-full px-2">
            <label
              className="block uppercase text-gray-700 text-xs font-bold mb-2"
              htmlFor="premises_address_line_2"
            >
              Address Line 2
            </label>
            <input
              type="text"
              className="border-0 px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
              placeholder="Address Line 2"
              name="premises_address_line_2"
              onChange={handleChange("premises_address_line_2")}
              value={form.premises_address_line_2 || ""}
            />
          </div>
        </div>
        <div className="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div className="w-full px-2 md:w-1/2">
            <label
              className="block uppercase text-gray-700 text-xs font-bold mb-2"
              htmlFor="premises_suburb"
            >
              Suburb <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={
                premisesErrors.premises_suburb
                  ? " border-red-500 border-x border-y px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
                  : "border-0 px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
              }
              placeholder="Suburb"
              name="premises_suburb"
              onChange={handleChange("premises_suburb")}
              value={form.premises_suburb || ""}
            />
            <p className=" text-red-600 text-xs mt-2">
              {premisesErrors.premises_suburb}
            </p>
          </div>
          <div className="w-full px-2 md:w-1/4">
            <label
              className="block uppercase text-gray-700 text-xs font-bold mb-2"
              htmlFor="premises_state"
            >
              State
            </label>
            <input
              disabled={true}
              type="text"
              className="border-0 px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
              name="premises_state"
              value={form.premises_state || ""}
            />
          </div>
          <div className="w-full px-2 md:w-1/4">
            <label
              className="block uppercase text-gray-700 text-xs font-bold mb-2"
              htmlFor="premises_postcode"
            >
              Postcode <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              maxLength={4}
              className={
                premisesErrors.premises_postcode
                  ? " border-red-500 border-x border-y px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
                  : "border-0 px-3 py-3 text-gray-700 bg-gray-50 rounded text-xs shadow focus:outline-none focus:ring w-full"
              }
              placeholder="Postcode"
              name="premises_postCode"
              onChange={handleChange("premises_postcode")}
              value={form.premises_postcode || ""}
            />
            <p className=" text-red-600 text-xs mt-2">
              {premisesErrors.premises_postcode}
            </p>
          </div>
        </div>

        <span className="block uppercase text-gray-700 text-xs font-bold mb-2 mt-6">
          Options to select:
        </span>

        <div className="flex">
          <label className="inline-flex items-center text-xs">
            <input
              type="radio"
              name="type"
              value="Land"
              checked={form.type === "Land"}
              onChange={handleChange("type")}
            />
            <span className="ml-2 text-xs">Private Property Sale Pack</span>
          </label>

          <label className="inline-flex items-center sm:ml-2 ml-6">
            <input
              type="radio"
              name="type"
              value="Strata"
              checked={form.type === "Strata"}
              onChange={handleChange("type")}
            />
            <span className="ml-2 text-xs">
              Strata Titled Property Sale pack
            </span>
          </label>
        </div>
      </form>

      <div className="text-center flex justify-between mt-6">
        <button
          className="text-white bg-black focus:ring-4 font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2"
          type="button"
          onClick={prevStep}
        >
          Back
        </button>

        <button
          className="text-white bg-sky-500 focus:ring-4 font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2"
          type="button"
          onClick={onSubmitHandler}
        >
          Next
        </button>
      </div>
      <p className={unexpectedError ? "text-red-600 text-s mt-2" : "hidden"}>
        {unexpectedError.message}
      </p>
    </div>
  );
}
